import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Options, SeriesOptionsType } from 'highcharts';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { CropFieldTrendLineResponse, DataPointDto, GetCropFieldTrendLineResponse } from 'src/app/bem/shared/models/models';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ChartComponent } from '@next/core-lib/chart';

const NO_OF_CHARTS = 2; // Leaf weight and nitrate

export interface ChartOptionsWithLocaleService extends Options {
  localeService?: LocaleService;
}

@Component({
    selector: 'app-trendline-graphs',
    templateUrl: './trendline-graphs.component.html',
    styleUrls: ['./trendline-graphs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        NgTemplateOutlet,
        NotificationEmptyComponent,
        MatProgressSpinner,
        ChartComponent,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class TrendlineGraphsComponent implements OnInit, OnDestroy {
  private readonly localeService = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);

  @Input() trendLineData$: Observable<GetCropFieldTrendLineResponse | null> | undefined;

  leafWeightChartData: SeriesOptionsType[] = [];
  leafWeightChartOptions!: ChartOptionsWithLocaleService;

  nitrateChartData: SeriesOptionsType[] = [];
  nitrateChartOptions!: ChartOptionsWithLocaleService;

  private readonly destroy$ = new Subject<void>();
  private latestTrendLineResponse?: CropFieldTrendLineResponse;
  private chartsInitialized = 0;
  private resizeObserver: ResizeObserver | null = null;

  ngOnInit(): void {
    if (this.trendLineData$) {
      this.trendLineData$.pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          if (this.isSuccessResponse(response)) {
            this.latestTrendLineResponse = response;
            this.setupCharts();
            this.cdr.detectChanges();
          }
        }),
      ).subscribe();
    }

    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.latestTrendLineResponse) {
        this.setupCharts();
        this.cdr.detectChanges();
      }
    });
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  isSuccessResponse(response: GetCropFieldTrendLineResponse | null): response is CropFieldTrendLineResponse {
    return !!response && 'leafWeightGraph' in response && 'nitrateGraph' in response;
  }

  private setupCharts() {
    this.chartsInitialized = 0;
    if (!this.latestTrendLineResponse) return;
    this.setupLeafWeightChart(this.latestTrendLineResponse);
    this.setupNitrateChart(this.latestTrendLineResponse);
  }

  private setupLeafWeightChart(data: CropFieldTrendLineResponse): void {
    const samplePointsData = this.mapDataPoints(data.leafWeightGraph.samplePoints);
    const trendLineData = this.mapDataPoints(data.leafWeightGraph.trendLine);

    this.leafWeightChartData = [
      {
        name: this.localeService.translate('BFP.UI.TrendlineData.SamplePoints'),
        type: 'scatter',
        data: samplePointsData,
        color: '#32CD32',
        marker: {
          symbol: 'circle',
          radius: 6,
        },
      },
      {
        name: this.localeService.translate('BFP.UI.TrendlineData.PredictedGrowth'),
        type: 'line',
        data: trendLineData,
        color: '#006400',
        lineWidth: 2,
        marker: {
          enabled: false,
        },
      },
    ];

    this.leafWeightChartOptions = {
      chart: {
        height: 300,
        events: {
          load: () => this.onChartLoad(),
        },
      },
      title: {
        text: this.localeService.translate('BFP.UI.TrendlineData.LeafWeightTitle'),
      },
      xAxis: {
        title: {
          text: this.localeService.translate('BFP.UI.TrendlineData.DaysAfterEmergence'),
        },
      },
      yAxis: {
        title: {
          text: this.localeService.translate('BFP.UI.TrendlineData.LeafWeight'),
        },
      },
      tooltip: {
        formatter() {
          const seriesName = this.series.name;
          const xCoord = this.x.toString();
          const yCoord = this.y.toFixed(2);

          const localeService = (this.series.chart.userOptions as ChartOptionsWithLocaleService).localeService;
          return localeService?.translate(
            'BFP.UI.TrendlineData.LeafWeightGraph.ToolTip',
            { seriesName, xCoord, yCoord },
          );
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      localeService: this.localeService,
      credits: {
        enabled: false,
      },
    };
  }

  private setupNitrateChart(data: CropFieldTrendLineResponse): void {
    const samplePointsData = this.mapDataPoints(data.nitrateGraph.samplePoints);
    const trendLineData = this.mapDataPoints(data.nitrateGraph.trendLine);

    this.nitrateChartData = [
      {
        name: this.localeService.translate('BFP.UI.TrendlineData.SamplePoints'),
        type: 'scatter',
        data: samplePointsData,
        color: '#32CD32',
        marker: {
          symbol: 'circle',
          radius: 6,
        },
      },
      {
        name: this.localeService.translate('BFP.UI.TrendlineData.PredictedNitrate'),
        type: 'line',
        data: trendLineData,
        color: '#006400',
        lineWidth: 2,
        marker: {
          enabled: false,
        },
      },
    ];

    this.nitrateChartOptions = {
      chart: {
        height: 300,
        events: {
          load: () => this.onChartLoad(),
        },
      },
      title: {
        text: this.localeService.translate('BFP.UI.TrendlineData.leafNitrateTitle'),
      },
      xAxis: {
        title: {
          text: this.localeService.translate('BFP.UI.TrendlineData.DaysAfterEmergence'),
        },
      },
      yAxis: {
        title: {
          text: this.localeService.translate('BFP.UI.TrendlineData.LeafNitrate'),
        },
      },
      tooltip: {
        formatter() {
          const seriesName = this.series.name;
          const xCoord = this.x.toString();
          const yCoord = this.y.toFixed(2);

          const localeService = (this.series.chart.userOptions as ChartOptionsWithLocaleService).localeService!;
          return localeService.translate(
            'BFP.UI.TrendlineData.LeafNitrateGraph.ToolTip',
            { seriesName, xCoord, yCoord },
          );
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      localeService: this.localeService,
      credits: {
        enabled: false,
      },
    };
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.latestTrendLineResponse) {
        this.cdr.detectChanges();
      }
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  private onChartLoad(): void {
    this.chartsInitialized++;
    if (this.chartsInitialized === NO_OF_CHARTS) {
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 10);
    }
  }

  private mapDataPoints(dataPoints: DataPointDto[]): [number, number][] {
    return dataPoints.map((point) => [point.index, point.value]);
  }
}
